import * as React from 'react';
import { useQueryClient } from 'react-query';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Button,
  Input,
  LoadingIcon,
  Modal,
  Tooltip,
} from 'vet-bones/design-system/components';

import { useAppDispatch } from 'src/ui/app/hooks';
import { Loading } from 'src/ui/components/Loading';
import { NotFound } from 'src/ui/components/NotFound';
import {
  INVITATION_DIALOG_CANCEL,
  INVITATION_DIALOG_NAME_INPUT,
  INVITATION_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import { useInvalidateConnections } from 'src/ui/containers/endpoints/utils/invalidate';
import { Connection, ConnectionStatus } from 'src/ui/features/connection';
import { queueNotification } from 'src/ui/features/notifications';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  AddConnectionMutation,
  useAcceptInvitationMutation,
  useAddConnectionMutation,
  useGetInvitationQuery,
  useListOrganizationsQuery,
  useRenameMySharedConnectionMutation,
  useSavedConnectionsQuery,
  useUserPartnerConnectionsQuery,
  useVerifyInvitationMutation,
} from 'src/ui/graph/types';
import {
  useClearConnection,
  useNavigateToConnection,
  useNavigateToPartnerConnection,
  useProfile,
  useVerifyConnection,
} from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import * as copy from 'src/ui/templates/copy';
import { buildDashboardRoute, buildRoute } from 'src/ui/utils/buildRoute';
import { getHostnameFromUrl } from 'src/ui/utils/getHostnameFromUrl';

enum InvitationType {
  ENDPOINT = 'ENDPOINT',
  CONNECTION = 'CONNECTION',
  ORGANIZATION = 'ORGANIZATION',
}
type InvitationPathParams = { id: string };

const EndpointInvitation: React.VFC<{
  endpoint: string;
  name: string;
  onSetName: (name: string) => void;
  sender: string;
}> = ({ endpoint, name, onSetName, sender }) => (
  <>
    <div>{copy.components.invitation.endpoint.info(sender, endpoint)}</div>
    <Input
      autoFocus
      id="endpoint-name"
      label={copy.components.invitation.endpoint.name}
      onChange={(e) => onSetName(e.target.value)}
      size="md"
      testId={INVITATION_DIALOG_NAME_INPUT}
      value={name}
    />
  </>
);

export const Invitation: React.VFC = () => {
  const { data: profileData, isLoading: profileIsLoading } = useProfile();
  const pathParams: InvitationPathParams = useParams();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();

  const onError = useOnError();
  const onMutate = useOnMutate();
  const dispatch = useAppDispatch();
  const clearConnection = useClearConnection();
  const navigateToConnection = useNavigateToConnection();
  const navigateToPartnerConnection = useNavigateToPartnerConnection();
  const invalidateConnections = useInvalidateConnections('');

  const { isLoading: isValidating, mutate: validate } = useVerifyConnection();
  const {
    data: invitationData,
    isLoading: invitationIsLoading,
  } = useGetInvitationQuery(graphQLClient, {
    id: pathParams.id,
  });
  const { data: partnerInfo } = useUserPartnerConnectionsQuery(graphQLClient);
  const { mutate: verifyInvitation } = useVerifyInvitationMutation(
    graphQLClient
  );
  const {
    isLoading: isAccepting,
    mutate: acceptInvitation,
  } = useAcceptInvitationMutation<Error>(graphQLClient);
  const {
    data: savedConnections,
    isLoading: isSavedConnectionsLoading,
    refetch: refetchSavedConnections,
  } = useSavedConnectionsQuery(
    graphQLClient,
    { org_domain: '' },
    { enabled: Boolean(profileData?.profile?.is_authenticated) }
  );
  const {
    isLoading: isAddingConnection,
    mutate: addConnection,
  } = useAddConnectionMutation(graphQLClient, {
    onError,
    onMutate,
    onSuccess: (c: AddConnectionMutation) => {
      invalidateConnections();

      if (!c.addConnection) {
        clearConnection();
        return;
      }

      acceptInvitation({ input: { id: pathParams.id } });

      if (partnerInfo?.userPartnerConnections?.length) {
        navigateToPartnerConnection(`/databricks-partner-home`);
        return;
      }

      navigateToConnection(c.addConnection.index);
    },
  });
  const {
    mutateAsync: renameMySharedConnection,
  } = useRenameMySharedConnectionMutation(graphQLClient);

  // Verify the invitation, so we know the email has been verified
  React.useEffect(() => {
    if (invitationData?.getInvitation) {
      verifyInvitation({
        input: {
          id: pathParams.id,
        },
      });
    }
  }, [invitationData, pathParams, verifyInvitation]);

  const { email, endpoint, connection, organization, sender } =
    invitationData?.getInvitation || {};

  let invitationType: InvitationType | null = null;
  if (endpoint) {
    invitationType = InvitationType.ENDPOINT;
  } else if (connection) {
    invitationType = InvitationType.CONNECTION;
  } else if (organization) {
    invitationType = InvitationType.ORGANIZATION;
  }

  const [endpointName, setEndpointName] = React.useState('');
  const [connectionName, setConnectionName] = React.useState('');

  React.useEffect(() => {
    if (endpoint) {
      setEndpointName(getHostnameFromUrl(endpoint));
    }

    if (connection) {
      setConnectionName(connection.name);
    }
  }, [connection, endpoint]);

  if (invitationIsLoading || profileIsLoading) {
    return <Loading />;
  }
  if (!invitationData?.getInvitation) {
    return (
      <NotFound
        description={copy.components.invitationNotFound.description}
        title={copy.components.invitationNotFound.title}
      />
    );
  }

  if (!email || !(endpoint || connection || organization)) {
    dispatch(queueNotification('Invalid Invitation'));
    return <Redirect to="/" />;
  }

  const existingUser = invitationData.getInvitation.existing_user;

  // Handle redirects after a successful login
  const next = location.pathname || '/';
  const search = location.search || '';
  const signupFlagParam = !existingUser ? '&signup=true' : '';
  const redirectLogin = {
    pathname: '/login',
    search: `?next=${next}${search}${signupFlagParam}`,
  };

  const isLoggedIn = profileData?.profile?.is_authenticated;

  if (isLoggedIn && profileData?.profile?.email !== email) {
    // TODO: This actually doesn't work properly. returnTo doesn't do anything. Figure out how to redirect to the invitation page after login.
    const redirectLogout = `/auth/logout?returnTo=${next}${search}`;

    return (
      <Modal
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
        className="invitation-dialog"
        footerActions={
          <Button href={redirectLogout} size="sm" variant="primary-accent">
            {copy.components.invitationDifferentUser.logoutButton}
          </Button>
        }
        headerSize="lg"
        isCloseButtonShown={false}
        isOpen
        title={copy.components.invitationDifferentUser.title}
      >
        <p>
          {copy.components.invitationDifferentUser.info(
            email,
            profileData?.profile?.email || ''
          )}
        </p>
        <p>{copy.components.invitationDifferentUser.instruction(email)}</p>
      </Modal>
    );
  }

  const handleSubmit = () => {
    if (!isLoggedIn) {
      history.push(redirectLogin);
      return;
    }

    if (invitationType === InvitationType.ENDPOINT) {
      const connection: Connection = {
        endpoint: endpoint || '',
        name: endpointName,
        username: email,
        useSSO: true,
        status: ConnectionStatus.PENDING,
      };

      validate(connection, {
        onError: (e) => {
          onError({
            ...e,
            message: copy.components.invitation.notifications.connectionFailure(
              e.message
            ),
          });
        },
        onSuccess: (data) => {
          addConnection({ input: data });
        },
      });
    } else if (
      invitationType === InvitationType.CONNECTION ||
      invitationType === InvitationType.ORGANIZATION
    ) {
      acceptInvitation(
        { input: { id: pathParams.id } },
        {
          onError: (e) => {
            onError({
              ...e,
              message: copy.components.invitation.notifications.acceptFailure(
                e.message
              ),
            });
          },
          onSuccess: async (data) => {
            if (!data.acceptInvitation?.success) {
              onError({
                message: copy.components.invitation.notifications.acceptFailure(
                  data.acceptInvitation?.error || ''
                ),
              });
              return;
            }

            if (invitationType === InvitationType.CONNECTION) {
              if (connection?.id) {
                await renameMySharedConnection({
                  input: {
                    connectionId: connection?.id,
                    name: connectionName,
                  },
                });
              }
              invalidateConnections();
              const updatedConns = await refetchSavedConnections();
              const conn = updatedConns.data?.listConnections?.find(
                (c) => c?.id === connection?.id
              );
              history.push(buildRoute('', conn?.index ?? ''));
            } else if (invitationType === InvitationType.ORGANIZATION) {
              await queryClient.invalidateQueries(
                useListOrganizationsQuery.getKey()
              );
              history.push(buildDashboardRoute(organization?.domain || ''));
            }
          },
        }
      );
    }
  };

  const isWorking =
    isAccepting ||
    (isLoggedIn && isSavedConnectionsLoading) ||
    isValidating ||
    isAddingConnection;

  let submitTooltipText = '';
  if (invitationType === InvitationType.ENDPOINT) {
    if (!endpointName) {
      submitTooltipText =
        copy.components.invitation.endpoint.validationRequired;
    } else if (
      savedConnections?.listConnections?.find((c) => c?.name === endpointName)
    ) {
      submitTooltipText = copy.components.invitation.endpoint.validationExists;
    }
  } else if (invitationType === InvitationType.CONNECTION) {
    if (!connectionName) {
      submitTooltipText =
        copy.components.invitation.endpoint.validationRequired;
    } else if (
      savedConnections?.listConnections?.find((c) => c?.name === connectionName)
    ) {
      submitTooltipText = copy.components.invitation.endpoint.validationExists;
    }
  }
  const isSubmitDisabled = Boolean(submitTooltipText);

  let submitLabel: string;
  if (isLoggedIn) {
    submitLabel = copy.components.invitation.acceptButton;
  } else if (existingUser) {
    submitLabel = copy.components.invitation.loginButton;
  } else {
    submitLabel = copy.components.invitation.signupButton;
  }

  return (
    <Modal
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className="invitation-dialog"
      footerActions={
        <>
          <Button
            onClick={() => history.push('/')}
            size="sm"
            testId={INVITATION_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.invitation.cancelButton}
          </Button>
          <Tooltip
            description={submitTooltipText}
            disabled={!submitTooltipText}
          >
            <Button
              disabled={isSubmitDisabled || isWorking}
              leftIcon={isWorking ? LoadingIcon : undefined}
              onClick={handleSubmit}
              size="sm"
              testId={INVITATION_DIALOG_SUBMIT}
              variant="primary-accent"
            >
              {submitLabel}
            </Button>
          </Tooltip>
        </>
      }
      headerSize="lg"
      isCloseButtonShown={false}
      isOpen
      title={copy.components.invitation.title}
    >
      {invitationType === InvitationType.ENDPOINT ? (
        <EndpointInvitation
          endpoint={endpoint || ''}
          name={endpointName}
          onSetName={setEndpointName}
          sender={sender?.first_name || ''}
        />
      ) : null}
      {invitationType === InvitationType.CONNECTION ? (
        <EndpointInvitation
          endpoint={connection?.endpoint || ''}
          name={connectionName}
          onSetName={setConnectionName}
          sender={sender?.first_name || ''}
        />
      ) : null}
      {invitationType === InvitationType.ORGANIZATION ? (
        <div>
          {copy.components.invitation.organization.info(
            sender?.first_name || '',
            organization?.domain || ''
          )}
        </div>
      ) : null}
    </Modal>
  );
};
